/**
 * Diagrames entitat-relació en notació de Chen, com els dels apunts (uni.f.cat, src/render.ts).
 * Una cadena es llegeix "ENT: Id, Attr | 1 | verb + AttrRel | N | ENT: Id, Attr …" i el primer
 * atribut de cada entitat és l'identificador. En surten dos dibuixos: un d'ample per a pantalles
 * grans i un de llarg per al mòbil, perquè el text no s'hagi d'encongir.
 */

type Ent = { name: string; attrs: string[] };
type Rel = { name: string; attr?: string; ca: string; cb: string };

export const CHEN_CSS = `
.chen { container-type: inline-size; }
.chen svg { display: block; width: 100%; height: auto; margin: 0 auto; overflow: visible; }
.chen-v { display: none !important; }
@container (max-width: 520px) { .chen-d .chen-h { display: none; } .chen-d .chen-v { display: block !important; } }
@container (max-width: 760px) { .chen-d.wide .chen-h { display: none; } .chen-d.wide .chen-v { display: block !important; } }
.chen-d + .chen-d { margin-top: 18px; padding-top: 18px; border-top: 1px dashed var(--border); }
.chen line { stroke: var(--muted-foreground); stroke-opacity: .6; stroke-width: 1.4; }
.chen line.dash { stroke-dasharray: 4 3; }
.chen text { fill: var(--foreground); font: 500 12.5px ui-sans-serif, system-ui, sans-serif; text-anchor: middle; }
.c-ent rect { fill: color-mix(in oklab, var(--acc) 12%, var(--background)); stroke: var(--acc); stroke-width: 1.6; }
.c-ent text { fill: var(--acc); font-weight: 700; font-size: 13px; letter-spacing: .02em; }
.c-rel path { fill: var(--background); stroke: var(--acc); stroke-width: 1.6; }
.c-rel text { font-style: italic; font-weight: 600; }
.c-att ellipse { fill: var(--secondary); stroke: var(--border); stroke-width: 1.3; }
.c-att text { fill: var(--muted-foreground); }
.c-att.id text { fill: var(--foreground); font-weight: 600; }
.c-att.id line { stroke: var(--foreground); stroke-opacity: 1; stroke-width: 1.1; }
.c-att.rel-att ellipse { fill: color-mix(in oklab, var(--warn) 14%, var(--background)); stroke: var(--warn); stroke-dasharray: 4 3; }
.c-att.rel-att text { fill: var(--foreground); }
.chen .c-card { fill: var(--acc); font: 700 13px ui-monospace, monospace; }
`;

const textW = (s: string, size: number, bold = false) => s.length * size * (bold ? 0.66 : 0.56);
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function parse(line: string) {
  const parts = line.split("|").map((p) => p.trim());
  const ents: Ent[] = [];
  const rels: Rel[] = [];
  for (let i = 0; i < parts.length; i += 4) {
    const [name, attrs = ""] = parts[i]!.split(":").map((s) => s.trim());
    ents.push({ name: name!, attrs: attrs ? attrs.split(",").map((a) => a.trim()) : [] });
    if (i + 4 < parts.length) {
      const [rel, attr] = parts[i + 2]!.split("+").map((s) => s.trim());
      rels.push({ name: rel!, attr, ca: parts[i + 1]!, cb: parts[i + 3]! });
    }
  }
  return { ents, rels };
}

type Shape = { kind: "ent" | "rel" | "att" | "ratt"; x: number; y: number; w: number; h: number; text: string; id?: boolean };
type Edge = { x1: number; y1: number; x2: number; y2: number; dash?: boolean };
type Label = { x: number; y: number; text: string };

function svg({ ents, rels }: ReturnType<typeof parse>, down: boolean) {
  const shapes: Shape[] = [];
  const edges: Edge[] = [];
  const labels: Label[] = [];
  const entBox = (e: Ent) => ({ w: Math.max(104, textW(e.name, 13, true) + 28), h: 42 });
  const relBox = (r: Rel) => ({ w: Math.max(116, textW(r.name, 12.5) + 56), h: 62 });
  const attBox = (a: string) => ({ w: textW(a, 12.5) + 26, h: 32 });
  const GAP = down ? 34 : 46;
  let pos = 0;
  const place = (i: number) => {
    const e = ents[i]!;
    const box = entBox(e);
    const half = Math.ceil(e.attrs.length / 2);
    const rowsA = e.attrs.slice(0, half);
    const rowsB = e.attrs.slice(half);
    if (!down) {
      const rowW = (r: string[]) => r.reduce((s, a) => s + attBox(a).w, 0) + Math.max(0, r.length - 1) * 10;
      const slot = Math.max(box.w, rowW(rowsA), rowW(rowsB));
      const cx = pos + slot / 2;
      shapes.push({ kind: "ent", x: cx, y: 0, ...box, text: e.name });
      [rowsA, rowsB].forEach((row, r) => {
        let x = cx - rowW(row) / 2;
        const y = r === 0 ? -92 : 92;
        for (const a of row) {
          const b = attBox(a);
          shapes.push({ kind: "att", x: x + b.w / 2, y, ...b, text: a, id: a === e.attrs[0] });
          edges.push({ x1: cx, y1: r === 0 ? -box.h / 2 : box.h / 2, x2: x + b.w / 2, y2: y + (r === 0 ? b.h / 2 : -b.h / 2) });
          x += b.w + 10;
        }
      });
      pos += slot;
      return { lo: cx - box.w / 2, hi: cx + box.w / 2 };
    }
    const colH = (r: string[]) => r.length * 32 + Math.max(0, r.length - 1) * 10;
    const slot = Math.max(box.h, colH(rowsA), colH(rowsB));
    const cy = pos + slot / 2;
    shapes.push({ kind: "ent", x: 0, y: cy, ...box, text: e.name });
    [rowsA, rowsB].forEach((col, c) => {
      let y = cy - colH(col) / 2;
      for (const a of col) {
        const b = attBox(a);
        const x = c === 0 ? -box.w / 2 - 22 - b.w / 2 : box.w / 2 + 22 + b.w / 2;
        shapes.push({ kind: "att", x, y: y + 16, ...b, text: a, id: a === e.attrs[0] });
        edges.push({ x1: c === 0 ? -box.w / 2 : box.w / 2, y1: cy, x2: x + (c === 0 ? b.w / 2 : -b.w / 2), y2: y + 16 });
        y += 42;
      }
    });
    pos += slot;
    return { lo: cy - box.h / 2, hi: cy + box.h / 2 };
  };
  let prev = place(0);
  rels.forEach((r, i) => {
    const box = relBox(r);
    const att = r.attr ? attBox(r.attr) : null;
    pos += GAP;
    const slot = down ? box.h : Math.max(box.w, att ? att.w : 0);
    const c = pos + slot / 2;
    const [x, y] = down ? [0, c] : [c, 0];
    shapes.push({ kind: "rel", x, y, ...box, text: r.name });
    if (att) {
      const [ax, ay] = down ? [box.w / 2 + 30 + att.w / 2, c] : [c, 86];
      shapes.push({ kind: "ratt", x: ax, y: ay, ...att, text: r.attr! });
      edges.push(down ? { x1: box.w / 2, y1: c, x2: ax - att.w / 2, y2: c, dash: true } : { x1: c, y1: box.h / 2, x2: c, y2: ay - att.h / 2, dash: true });
    }
    pos += slot + GAP;
    const next = place(i + 1);
    const a0 = prev.hi, a1 = c - (down ? box.h : box.w) / 2;
    const b0 = c + (down ? box.h : box.w) / 2, b1 = next.lo;
    if (down) {
      edges.push({ x1: 0, y1: a0, x2: 0, y2: a1 }, { x1: 0, y1: b0, x2: 0, y2: b1 });
      labels.push({ x: -12, y: (a0 + a1) / 2 + 5, text: r.ca }, { x: -12, y: (b0 + b1) / 2 + 5, text: r.cb });
    } else {
      edges.push({ x1: a0, y1: 0, x2: a1, y2: 0 }, { x1: b0, y1: 0, x2: b1, y2: 0 });
      labels.push({ x: (a0 + a1) / 2, y: -8, text: r.ca }, { x: (b0 + b1) / 2, y: -8, text: r.cb });
    }
    prev = next;
  });
  const P = 6;
  const minX = Math.min(...shapes.map((s) => s.x - s.w / 2)) - P;
  const maxX = Math.max(...shapes.map((s) => s.x + s.w / 2)) + P;
  const minY = Math.min(...shapes.map((s) => s.y - s.h / 2)) - P;
  const maxY = Math.max(...shapes.map((s) => s.y + s.h / 2)) + P;
  const w = maxX - minX, h = maxY - minY;
  const f = (n: number) => Math.round(n * 10) / 10;
  const body = [
    ...edges.map((e) => `<line x1="${f(e.x1)}" y1="${f(e.y1)}" x2="${f(e.x2)}" y2="${f(e.y2)}"${e.dash ? ' class="dash"' : ""}/>`),
    ...shapes.map((s) => {
      const t = esc(s.text);
      if (s.kind === "ent") return `<g class="c-ent"><rect x="${f(s.x - s.w / 2)}" y="${f(s.y - s.h / 2)}" width="${f(s.w)}" height="${s.h}" rx="5"/><text x="${f(s.x)}" y="${f(s.y + 4.5)}">${t}</text></g>`;
      if (s.kind === "rel") return `<g class="c-rel"><path d="M${f(s.x - s.w / 2)} ${f(s.y)}L${f(s.x)} ${f(s.y - s.h / 2)}L${f(s.x + s.w / 2)} ${f(s.y)}L${f(s.x)} ${f(s.y + s.h / 2)}Z"/><text x="${f(s.x)}" y="${f(s.y + 4.5)}">${t}</text></g>`;
      const tw = textW(s.text, 12.5);
      return `<g class="c-att${s.kind === "ratt" ? " rel-att" : ""}${s.id ? " id" : ""}"><ellipse cx="${f(s.x)}" cy="${f(s.y)}" rx="${f(s.w / 2)}" ry="${s.h / 2}"/><text x="${f(s.x)}" y="${f(s.y + 4.3)}">${t}</text>${s.id ? `<line x1="${f(s.x - tw / 2)}" y1="${f(s.y + 7.5)}" x2="${f(s.x + tw / 2)}" y2="${f(s.y + 7.5)}"/>` : ""}</g>`;
    }),
    ...labels.map((l) => `<text class="c-card" x="${f(l.x)}" y="${f(l.y)}"${down ? ' text-anchor="end"' : ""}>${esc(l.text)}</text>`),
  ].join("");
  return { w, html: `<svg class="chen-${down ? "v" : "h"}" viewBox="${f(minX)} ${f(minY)} ${f(w)} ${f(h)}" style="max-width:${Math.ceil(w)}px" aria-hidden="true">${body}</svg>` };
}

export function Chen({ chains, alt }: { chains: string[]; alt: string }) {
  return (
    <figure className="chen rounded-xl border border-border p-4 sm:p-5">
      {chains.map((line) => {
        const c = parse(line);
        const h = svg(c, false);
        const v = svg(c, true);
        return <div key={line} className={`chen-d${h.w > 640 ? " wide" : ""}`} role="img" aria-label={`Diagrama ER: ${alt}`} dangerouslySetInnerHTML={{ __html: h.html + v.html }} />;
      })}
      <figcaption className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><i className="inline-block w-4 h-[11px] rounded-[2px] border-[1.5px]" style={{ borderColor: "var(--acc)", background: "color-mix(in oklab, var(--acc) 12%, var(--background))" }} />Entitat</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block size-2.5 mx-[3px] rotate-45 border-[1.5px] bg-background" style={{ borderColor: "var(--acc)" }} />Relació</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block w-4 h-[11px] rounded-full border-[1.5px] border-border bg-secondary" />Atribut</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block w-4 h-[11px] rounded-full border-[1.5px] border-dashed" style={{ borderColor: "var(--warn)" }} />Atribut de la relació</span>
        <span><u className="text-foreground underline-offset-2">Subratllat</u> = identificador</span>
      </figcaption>
    </figure>
  );
}
